// Pattern geometry (docs/04 §3). Every instance of every pattern for a given grid size.
import { definePattern, type PatternDef } from './defs';
import type { Cell } from '../sim/types';

const c = (reel: number, row: number): Cell => ({ reel, row });

function runs(len: number, reels: number, rows: number): Cell[][] {
  const out: Cell[][] = [];
  if (reels < len) return out;
  for (let row = 0; row < rows; row++)
    for (let start = 0; start + len <= reels; start++)
      out.push(Array.from({ length: len }, (_, i) => c(start + i, row)));
  return out;
}

export const PATTERN_LIST: PatternDef[] = [
  definePattern({
    id: 'pair',
    family: 'line',
    geometry: (reels, rows) =>
      reels < 2 ? [] : Array.from({ length: rows }, (_, row) => [c(0, row), c(1, row)]),
  }),
  definePattern({ id: 'line3', family: 'line', geometry: (reels, rows) => runs(3, reels, rows) }),
  definePattern({ id: 'line4', family: 'line', geometry: (reels, rows) => runs(4, reels, rows) }),
  definePattern({
    id: 'line5',
    family: 'line',
    geometry: (reels, rows) => (reels === 5 ? runs(5, reels, rows) : []),
  }),
  definePattern({
    id: 'col3',
    family: 'col',
    geometry: (reels, rows) => {
      const out: Cell[][] = [];
      if (rows < 3) return out;
      for (let reel = 0; reel < reels; reel++)
        for (let start = 0; start + 3 <= rows; start++) out.push([c(reel, start), c(reel, start + 1), c(reel, start + 2)]);
      return out;
    },
  }),
  definePattern({
    id: 'diag3',
    family: 'diag',
    geometry: (reels, rows) => {
      const out: Cell[][] = [];
      if (rows < 3) return out;
      for (let r0 = 0; r0 + 3 <= reels; r0++)
        for (let y0 = 0; y0 + 3 <= rows; y0++) {
          out.push([c(r0, y0), c(r0 + 1, y0 + 1), c(r0 + 2, y0 + 2)]);
          out.push([c(r0, y0 + 2), c(r0 + 1, y0 + 1), c(r0 + 2, y0)]);
        }
      return out;
    },
  }),
  definePattern({
    id: 'vee',
    family: 'vee',
    geometry: (reels, rows) => {
      if (reels < 5 || rows < 3) return [];
      const out: Cell[][] = [];
      for (let y0 = 0; y0 + 3 <= rows; y0++) {
        out.push([c(0, y0), c(1, y0 + 1), c(2, y0 + 2), c(3, y0 + 1), c(4, y0)]);
        out.push([c(0, y0 + 2), c(1, y0 + 1), c(2, y0), c(3, y0 + 1), c(4, y0 + 2)]);
      }
      return out;
    },
  }),
  definePattern({
    id: 'zig',
    family: 'zig',
    geometry: (reels, rows) => {
      if (reels < 5 || rows < 3) return [];
      const out: Cell[][] = [];
      for (let y0 = 0; y0 + 2 <= rows; y0++) {
        out.push([c(0, y0), c(1, y0 + 1), c(2, y0), c(3, y0 + 1), c(4, y0)]);
        out.push([c(0, y0 + 1), c(1, y0), c(2, y0 + 1), c(3, y0), c(4, y0 + 1)]);
      }
      return out;
    },
  }),
  definePattern({
    id: 'eye',
    family: 'eye',
    geometry: (reels, rows) => {
      const out: Cell[][] = [];
      for (let x = 1; x < reels - 1; x++)
        for (let y = 1; y < rows - 1; y++) {
          const ring: Cell[] = [];
          for (let dx = -1; dx <= 1; dx++)
            for (let dy = -1; dy <= 1; dy++) if (dx || dy) ring.push(c(x + dx, y + dy));
          out.push(ring);
        }
      return out;
    },
  }),
  definePattern({
    id: 'cross',
    family: 'cross',
    geometry: (reels, rows) => {
      const out: Cell[][] = [];
      for (let x = 1; x < reels - 1; x++)
        for (let y = 1; y < rows - 1; y++) out.push([c(x, y - 1), c(x - 1, y), c(x, y), c(x + 1, y), c(x, y + 1)]);
      return out;
    },
  }),
  definePattern({
    id: 'gorge',
    family: 'gorge',
    jackpot: true,
    geometry: (reels, rows) => {
      if (rows < 3) return [];
      const all: Cell[] = [];
      for (let y = 0; y < rows; y++) for (let x = 0; x < reels; x++) all.push(c(x, y));
      return [all];
    },
  }),
  definePattern({
    id: 'bandit',
    family: 'bandit',
    jackpot: true,
    geometry: (reels, rows) => (reels === 3 && rows === 1 ? [[c(0, 0), c(1, 0), c(2, 0)]] : []),
  }),
];
